import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult, UseMutationResult} from '@tanstack/react-query';
import {useCallback, useState} from 'react';
import {fundRepresentativesApi} from '../api';
import {CreateFundRepresentativeDTO, FundRepresentativesFilter, FundRepresentativeWithMetaModel} from '../model';
import {useCreateFundRepresentative} from './useCreateFundRepresentative';

type FundRepresentativesFilterWithoutCursor = Omit<FundRepresentativesFilter, 'after'>;

type FundRepresentativesState = {
  getInfiniteFundRepresentatives: UseInfiniteQueryResult<InfiniteData<FundRepresentativeWithMetaModel>, Error>;
  filters: FundRepresentativesFilterWithoutCursor;
  updateFilter: (newFilter: Partial<FundRepresentativesFilterWithoutCursor>) => void;
  createMutation: UseMutationResult<unknown, Error, CreateFundRepresentativeDTO>;
};

const DEFAULT_PAGE_SIZE = 50;

export const useFundRepresentatives = (
  initialFilter: FundRepresentativesFilterWithoutCursor = {},
): FundRepresentativesState => {
  const [filters, setFilters] = useState<FundRepresentativesFilterWithoutCursor>({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  });

  const getInfiniteFundRepresentatives = useInfiniteQuery<
    FundRepresentativeWithMetaModel,
    Error,
    InfiniteData<FundRepresentativeWithMetaModel>,
    ['fund-representatives', 'infinite', FundRepresentativesFilterWithoutCursor],
    string | undefined
  >({
    queryKey: ['fund-representatives', 'infinite', filters],
    queryFn: ({pageParam}) => fundRepresentativesApi.getFundRepresentatives({...filters, after: pageParam}),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
  });

  const updateFilter = useCallback((newFilter: Partial<FundRepresentativesFilterWithoutCursor>) => {
    setFilters((prev) => ({...prev, ...newFilter}));
  }, []);

  const createMutation = useCreateFundRepresentative();

  return {
    getInfiniteFundRepresentatives,
    filters,
    updateFilter,
    createMutation,
  };
};
